import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Home,
  PackageCheck,
  Search,
  Truck,
  UserRoundX,
} from "lucide-react";

import { ApiError, formatCLP } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  TableWrap,
} from "../../components/ui";
import type { BadgeTone } from "../../components/ui";
import { getAuth, hasPermission } from "../../lib/auth";
import {
  MOTIVO_PADRON,
  listarEntregas,
  listarPeriodosEntrega,
  motivoLabel,
  obtenerResumenEntregas,
  registrarEntrega,
  type EntregaEnPanel,
  type EstadoEntrega,
} from "./api";

const NOMBRES_MES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
] as const;

function periodoLabel(mes: number, anio: number): string {
  return `${NOMBRES_MES[mes - 1] ?? mes} ${anio}`;
}

function fechaHora(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("es-CL", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
}

const TONO_ESTADO: Record<EstadoEntrega, BadgeTone> = {
  Entregada: "emerald",
  NoEntregada: "rose",
  Pendiente: "slate",
};

const ESTADOS: ReadonlyArray<{ value: EstadoEntrega | null; label: string }> = [
  { value: null, label: "Todas" },
  { value: "Pendiente", label: "Por entregar" },
  { value: "NoEntregada", label: "No entregadas" },
  { value: "Entregada", label: "Entregadas" },
];

/**
 * Entrega de boletas (recorrido E3).
 *
 * El operador marcaba las entregas en la PWA y nadie las miraba: la oficina
 * no sabía cuántas faltaban, cuáles habían vuelto sin entregar ni por qué.
 * Acá se ve el avance del reparto por ruta, se trabajan las que no se
 * pudieron entregar, y se marca "entregada en oficina" cuando el socio pasa
 * a pagar antes de que el operador llegue a su casa.
 */
export function EntregasPage(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const auth = getAuth();
  const puedeGestionar = hasPermission(auth, "entregas.gestionar");

  const [periodo, setPeriodo] = useState<{ mes: number; anio: number } | null>(null);
  const [rutaSel, setRutaSel] = useState<number | null>(null);
  const [estadoSel, setEstadoSel] = useState<EstadoEntrega | null>(null);
  const [soloPadron, setSoloPadron] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [marcando, setMarcando] = useState<EntregaEnPanel | null>(null);
  const [recibidoPor, setRecibidoPor] = useState("");

  const periodosQ = useQuery({
    queryKey: ["entregas-periodos"],
    queryFn: listarPeriodosEntrega,
  });

  // Default: el período más nuevo con boletas emitidas.
  useEffect(() => {
    if (periodo === null && periodosQ.data && periodosQ.data.length > 0) {
      const p = periodosQ.data[0]!;
      setPeriodo({ mes: p.mes, anio: p.anio });
    }
  }, [periodo, periodosQ.data]);

  const resumenQ = useQuery({
    queryKey: ["entregas-resumen", periodo?.mes, periodo?.anio],
    queryFn: () => obtenerResumenEntregas(periodo!.mes, periodo!.anio),
    enabled: periodo !== null,
  });

  const listadoKey = [
    "entregas",
    periodo?.mes,
    periodo?.anio,
    rutaSel,
    estadoSel,
    soloPadron,
  ] as const;
  const listadoQ = useQuery({
    queryKey: listadoKey,
    queryFn: () =>
      listarEntregas({
        mes: periodo!.mes,
        anio: periodo!.anio,
        rutaId: rutaSel,
        estado: estadoSel,
        motivo: soloPadron ? MOTIVO_PADRON : null,
      }),
    enabled: periodo !== null,
  });

  const marcarM = useMutation({
    mutationFn: (args: { lecturaId: number; recibidoPor: string }) =>
      registrarEntrega(args.lecturaId, {
        estado: "Entregada",
        recibido_por: args.recibidoPor,
        observacion: "Entregada en oficina",
      }),
    onSuccess: () => {
      toast.success("Boleta marcada como entregada.");
      setMarcando(null);
      void qc.invalidateQueries({ queryKey: ["entregas"] });
      void qc.invalidateQueries({ queryKey: ["entregas-resumen"] });
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "No se pudo registrar la entrega.");
    },
  });

  const items = useMemo(() => listadoQ.data ?? [], [listadoQ.data]);
  const filtrados = useMemo(() => {
    if (!filtro.trim()) return items;
    const f = filtro.trim().toLowerCase();
    return items.filter(
      (e) =>
        e.socio_nombre.toLowerCase().includes(f) ||
        e.direccion.toLowerCase().includes(f) ||
        (e.codigo_arranque ?? "").toLowerCase().includes(f) ||
        (e.numero_boleta ?? "").toLowerCase().includes(f) ||
        (e.socio_rut ?? "").toLowerCase().includes(f),
    );
  }, [items, filtro]);

  const resumen = resumenQ.data;

  function abrirMarcar(e: EntregaEnPanel): void {
    setMarcando(e);
    setRecibidoPor("");
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Entrega de boletas"
        icon={PackageCheck}
        subtitle="Cómo va el reparto casa por casa del período, y qué boletas volvieron sin entregar."
      />

      {periodosQ.isPending && <Cargando />}
      {periodosQ.isError && (
        <ErrorCarga error={periodosQ.error} onReintentar={() => void periodosQ.refetch()} />
      )}

      {periodosQ.data && periodosQ.data.length === 0 && (
        <EmptyState
          icon={PackageCheck}
          title="Todavía no hay boletas emitidas"
          description="El reparto arranca cuando se factura un cierre."
        />
      )}

      {periodosQ.data && periodosQ.data.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mr-1">
            Período
          </span>
          {periodosQ.data.map((p) => {
            const activo = periodo?.mes === p.mes && periodo?.anio === p.anio;
            return (
              <button
                key={`${p.anio}-${p.mes}`}
                type="button"
                aria-pressed={activo}
                onClick={() => {
                  setPeriodo({ mes: p.mes, anio: p.anio });
                  setRutaSel(null);
                }}
                className={
                  activo
                    ? "rounded-md border-2 border-brand-600 bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-900"
                    : "rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-600 hover:border-brand-300"
                }
              >
                {periodoLabel(p.mes, p.anio)}
              </button>
            );
          })}
        </div>
      )}

      {resumen && resumen.total > 0 && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi
              label="Boletas del período"
              valor={resumen.total}
              icono={<PackageCheck className="h-4 w-4" />}
              tono="slate"
            />
            <Kpi
              label="Entregadas"
              valor={resumen.entregadas}
              icono={<CheckCircle2 className="h-4 w-4" />}
              tono="emerald"
              nota={
                resumen.cerradas_por_sistema > 0
                  ? `${resumen.cerradas_por_sistema} cerradas por el sistema`
                  : undefined
              }
            />
            <Kpi
              label="No entregadas"
              valor={resumen.no_entregadas}
              icono={<AlertTriangle className="h-4 w-4" />}
              tono="rose"
            />
            <Kpi
              label="Por entregar"
              valor={resumen.pendientes}
              icono={<Truck className="h-4 w-4" />}
              tono="amber"
            />
          </div>

          {resumen.cerradas_por_sistema > 0 && (
            <p className="text-xs text-neutral-500">
              <strong className="tabular-nums">{resumen.cerradas_por_sistema}</strong>{" "}
              boletas se dieron por entregadas automáticamente al facturar el
              período siguiente: nadie pasó por esas casas.
            </p>
          )}

          {resumen.requieren_padron > 0 && (
            <button
              type="button"
              onClick={() => {
                setSoloPadron(true);
                setEstadoSel(null);
              }}
              className="flex w-full items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left hover:bg-amber-100"
            >
              <UserRoundX className="h-5 w-5 shrink-0 text-amber-600" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-amber-900">
                  {resumen.requieren_padron} boleta
                  {resumen.requieren_padron !== 1 ? "s" : ""} con socio que se mudó
                </div>
                <div className="text-xs text-amber-800">
                  Volver a pasar por la casa no lo resuelve: hay que finalizar el
                  contrato o cambiar el titular.
                </div>
              </div>
              <span className="text-xs font-medium text-amber-700">Ver →</span>
            </button>
          )}

          <Card padding="none" className="overflow-hidden">
            <div className="px-4 py-2.5 border-b border-neutral-100 text-sm font-semibold text-neutral-900">
              Avance por ruta
            </div>
            <ul className="divide-y divide-neutral-100">
              {resumen.por_ruta.map((r) => {
                const pct = r.total > 0 ? Math.round((r.entregadas / r.total) * 100) : 0;
                const activo = rutaSel === r.ruta_id;
                return (
                  <li key={r.ruta_id ?? "sin-ruta"}>
                    <button
                      type="button"
                      aria-pressed={activo}
                      onClick={() => setRutaSel(activo ? null : r.ruta_id)}
                      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-neutral-50 ${
                        activo ? "bg-brand-50" : ""
                      }`}
                    >
                      <div className="w-32 shrink-0 text-sm font-medium text-neutral-900">
                        {r.ruta_codigo ? `${r.ruta_codigo} · ${r.ruta_nombre}` : "Sin ruta"}
                      </div>
                      <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-emerald-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="w-28 shrink-0 text-right text-xs tabular-nums text-neutral-600">
                        {r.entregadas}/{r.total}
                        {r.no_entregadas > 0 && (
                          <span className="ml-1 text-rose-600">({r.no_entregadas} no)</span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>
        </>
      )}

      {periodo && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {ESTADOS.map((e) => {
              const activo = estadoSel === e.value && !soloPadron;
              return (
                <button
                  key={e.label}
                  type="button"
                  aria-pressed={activo}
                  onClick={() => {
                    setEstadoSel(e.value);
                    setSoloPadron(false);
                  }}
                  className={
                    activo
                      ? "rounded-full bg-brand-700 px-3 py-1 text-xs font-semibold text-white"
                      : "rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-medium text-neutral-600 hover:border-brand-300"
                  }
                >
                  {e.label}
                </button>
              );
            })}
            {soloPadron && (
              <button
                type="button"
                onClick={() => setSoloPadron(false)}
                className="rounded-full bg-amber-700 px-3 py-1 text-xs font-semibold text-white"
              >
                Mudanzas ✕
              </button>
            )}
            {rutaSel !== null && (
              <button
                type="button"
                onClick={() => setRutaSel(null)}
                className="rounded-full bg-neutral-700 px-3 py-1 text-xs font-semibold text-white"
              >
                Ruta filtrada ✕
              </button>
            )}
            <div className="relative ml-auto w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-neutral-500" />
              <Input
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                placeholder="Socio, dirección, boleta…"
                className="pl-8"
              />
            </div>
          </div>

          {listadoQ.isLoading && <Cargando />}
          {listadoQ.isError && (
            <ErrorCarga error={listadoQ.error} onReintentar={() => void listadoQ.refetch()} />
          )}

          {listadoQ.data && filtrados.length === 0 && (
            <EmptyState
              icon={Home}
              title="Sin boletas con ese filtro"
              description="Prueba con otro estado, otra ruta o limpia la búsqueda."
            />
          )}

          {filtrados.length > 0 && (
            <TableWrap label="Boletas del período">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Arranque</th>
                    <th className="px-3 py-2 text-left font-semibold">Socio</th>
                    <th className="px-3 py-2 text-left font-semibold">Dirección</th>
                    <th className="px-3 py-2 text-right font-semibold">Monto</th>
                    <th className="px-3 py-2 text-left font-semibold">Estado</th>
                    <th className="px-3 py-2 text-left font-semibold">Detalle</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {filtrados.map((e) => (
                    <tr key={e.lectura_id} className="hover:bg-neutral-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-mono text-xs text-neutral-700">
                          {e.codigo_arranque ?? `#${e.arranque_id}`}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {e.ruta_codigo ?? "sin ruta"}
                          {e.orden_ruta != null && ` · pos ${e.orden_ruta}`}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/socios/${e.socio_id}/cuenta`}
                          className="font-medium text-neutral-900 hover:text-brand-700"
                        >
                          {e.socio_nombre}
                        </Link>
                        <div className="text-xs text-neutral-500">
                          {e.numero_boleta ? `boleta ${e.numero_boleta}` : ""}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-neutral-600">{e.direccion}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-neutral-700">
                        {formatCLP(e.monto_total)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Badge tone={TONO_ESTADO[e.estado]}>
                          {e.estado === "NoEntregada" ? "No entregada" : e.estado}
                        </Badge>
                        {e.pagada && (
                          <div className="mt-0.5">
                            <Badge tone="emerald">Pagada</Badge>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-neutral-600">
                        {e.estado === "Entregada" && (
                          <>
                            <div>
                              {e.cerrada_por_sistema
                                ? "Cerrada por el sistema"
                                : `Recibió: ${e.recibido_por ?? "—"}`}
                            </div>
                            <div className="text-neutral-500">{fechaHora(e.fecha_entrega)}</div>
                          </>
                        )}
                        {e.estado === "NoEntregada" && (
                          <>
                            <div
                              className={
                                e.motivo_no_entrega === MOTIVO_PADRON
                                  ? "font-medium text-amber-700"
                                  : ""
                              }
                            >
                              {motivoLabel(e.motivo_no_entrega)}
                            </div>
                            <div className="text-neutral-500 tabular-nums">
                              {e.intentos} intento{e.intentos !== 1 ? "s" : ""}
                            </div>
                          </>
                        )}
                        {e.estado === "Pendiente" && (
                          <span className="text-neutral-500">Sin intentos</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {puedeGestionar && e.estado !== "Entregada" && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => abrirMarcar(e)}
                          >
                            Entregada en oficina
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          )}
        </div>
      )}

      <Modal
        open={marcando !== null}
        onClose={() => setMarcando(null)}
        title="Entregada en oficina"
        subtitle={marcando ? `${marcando.socio_nombre} · ${marcando.direccion}` : undefined}
        icon={PackageCheck}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setMarcando(null)}>
              Cancelar
            </Button>
            <Button
              disabled={!recibidoPor.trim() || marcarM.isPending}
              onClick={() =>
                marcando &&
                marcarM.mutate({
                  lecturaId: marcando.lectura_id,
                  recibidoPor: recibidoPor.trim(),
                })
              }
            >
              {marcarM.isPending ? "Guardando…" : "Registrar entrega"}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-neutral-600">
            Para cuando el socio pasa por el comité y se lleva su boleta del
            mesón: sale de la ronda del operador y no hay que ir a esa casa.
          </p>
          <label className="block text-sm font-medium text-neutral-700">
            ¿Quién la recibió?
            <Input
              value={recibidoPor}
              onChange={(e) => setRecibidoPor(e.target.value)}
              placeholder="Nombre de quien retiró la boleta"
              className="mt-1"
              autoFocus
            />
          </label>
        </div>
      </Modal>
    </div>
  );
}

function Kpi({
  label,
  valor,
  icono,
  tono,
  nota,
}: {
  label: string;
  valor: number;
  icono: JSX.Element;
  tono: "slate" | "emerald" | "rose" | "amber";
  nota?: string | undefined;
}): JSX.Element {
  const colores: Record<string, string> = {
    slate: "text-neutral-600 bg-neutral-100",
    emerald: "text-emerald-700 bg-emerald-50",
    rose: "text-rose-700 bg-rose-50",
    amber: "text-amber-700 bg-amber-50",
  };
  return (
    <Card>
      <div className="flex items-center gap-2">
        <span className={`inline-flex rounded-md p-1.5 ${colores[tono]}`}>{icono}</span>
        <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          {label}
        </span>
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums text-neutral-900">{valor}</div>
      {nota && <div className="text-xs text-neutral-500">{nota}</div>}
    </Card>
  );
}
